import React, { useEffect, useState, useRef } from "react";
import { Box, IconButton, Tooltip, Menu, MenuItem, useTheme } from "@mui/material";
import { tokens } from "../theme";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

export default function AgGridHeader(props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [sort, setSort] = useState(props.column.getSort());
  const [pinned, setPinned] = useState(props.column.getPinned());
  const [isFiltered, setIsFiltered] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const filterButtonRef = useRef(null);

  useEffect(() => {
    const checkFilter = () => {
      if (props.api) {
        const model = props.api.getColumnFilterModel(props.column.getColId());
        setIsFiltered(!!model);
      }
    };

    checkFilter();

    const onSortChanged = () => {
      setSort(props.column.getSort());
    };
    const onPinnedChanged = () => {
      setPinned(props.column.getPinned());
    };

    props.column.addEventListener("sortChanged", onSortChanged);
    props.column.addEventListener("leftChanged", onPinnedChanged);
    props.column.addEventListener("pinnedChanged", onPinnedChanged);

    if (props.api) {
      props.api.addEventListener("filterChanged", checkFilter);
    }

    return () => {
      props.column.removeEventListener("sortChanged", onSortChanged);
      props.column.removeEventListener("leftChanged", onPinnedChanged);
      props.column.removeEventListener("pinnedChanged", onPinnedChanged);
      if (props.api) {
        props.api.removeEventListener("filterChanged", checkFilter);
      }
    };
  }, [props.column, props.api]);

  // If it is the selection column, we do not want to render our custom header controls,
  // allowing AG Grid's native selection checkbox header to display without styling conflicts.
  if (props.column.getColId() === "rowSelection" || !props.displayName) {
    return null;
  }

  const handleSort = (event) => {
    const multiSort = event.shiftKey;
    props.progressSort(multiSort);
  };

  const handleFilterClick = (event) => {
    event.stopPropagation();
    if (props.showFilter && filterButtonRef.current) {
      props.showFilter(filterButtonRef.current);
    }
  };

  const handlePinButtonClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handlePinSelect = (event, nextPin) => {
    event.stopPropagation();
    setAnchorEl(null);
    props.api.applyColumnState({
      state: [{ colId: props.column.getColId(), pinned: nextPin }],
    });
  };

  const getPinTooltip = () => {
    if (pinned === "left") return "Pinned Left (Click to change/unpin)";
    if (pinned === "right") return "Pinned Right (Click to change/unpin)";
    return "Pin Column";
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        cursor: "pointer",
        userSelect: "none",
        "&:hover .pin-button, &:hover .filter-button": {
          opacity: 0.7,
        },
      }}
      onClick={handleSort}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flexGrow: 1,
        }}
      >
        <span className="ag-header-cell-text" style={{ marginRight: "4px" }}>
          {props.displayName}
        </span>

        {/* Sort Icons */}
        {sort === "asc" && (
          <ArrowUpwardIcon sx={{ fontSize: "14px", ml: "2px", opacity: 0.8 }} />
        )}
        {sort === "desc" && (
          <ArrowDownwardIcon sx={{ fontSize: "14px", ml: "2px", opacity: 0.8 }} />
        )}
      </Box>

      {/* Action Buttons Group */}
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {/* Filter Column Button */}
        {props.column.isFilterAllowed() && (
          <Tooltip title="Filter Column" placement="top">
            <IconButton
              ref={filterButtonRef}
              className="filter-button"
              size="small"
              onClick={handleFilterClick}
              sx={{
                padding: "2px",
                opacity: isFiltered ? 1 : 0,
                transition: "opacity 0.15s ease-in-out, color 0.15s ease-in-out",
                color: isFiltered ? (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200]) : "inherit",
                "&:hover": {
                  opacity: "1 !important",
                  color: isFiltered ? (theme.palette.mode === "dark" ? colors.grey[100] : colors.grey[100]) : colors.grey[300],
                },
              }}
            >
              <FilterAltIcon sx={{ fontSize: "14px" }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Pin Column Button */}
        {!props.suppressPinning && (
          <Tooltip title={getPinTooltip()} placement="top">
            <IconButton
              className="pin-button"
              size="small"
              onClick={handlePinButtonClick}
              sx={{
                padding: "2px",
                opacity: pinned ? 1 : 0,
                transition: "opacity 0.15s ease-in-out, color 0.15s ease-in-out",
                color: pinned ? (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200]) : "inherit",
                "&:hover": {
                  opacity: "1 !important",
                  color: pinned ? (theme.palette.mode === "dark" ? colors.grey[100] : colors.grey[100]) : colors.grey[300],
                },
              }}
            >
              {pinned ? (
                <PushPinIcon sx={{ fontSize: "14px" }} />
              ) : (
                <PushPinOutlinedIcon sx={{ fontSize: "14px" }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          style: {
            backgroundColor: colors.grey[800],
            color: colors.grey[100],
            border: `1px solid ${colors.grey[700]}`,
          },
        }}
      >
        <MenuItem
          onClick={(e) => handlePinSelect(e, "left")}
          selected={pinned === "left"}
          style={{ fontSize: "12px", padding: "6px 16px" }}
        >
          Pin Left
        </MenuItem>
        <MenuItem
          onClick={(e) => handlePinSelect(e, "right")}
          selected={pinned === "right"}
          style={{ fontSize: "12px", padding: "6px 16px" }}
        >
          Pin Right
        </MenuItem>
        <MenuItem
          onClick={(e) => handlePinSelect(e, null)}
          selected={pinned === null}
          style={{ fontSize: "12px", padding: "6px 16px" }}
        >
          Unpin
        </MenuItem>
      </Menu>
    </Box>
  );
}
